import { AnimatedModal } from "@/src/components/AnimatedModal";
import { CloseIcon } from "@/src/components/CloseIcon";
import { DefaultRoleTag } from "@/src/components/permissions/DefaultRoleTag";
import { DealPermissionsSummary } from "@/src/components/permissions/DealPermissionsSummary";
import {
  SettingsCard,
  SettingsEmpty,
  SettingsPage,
  SettingsRow,
} from "@/src/components/settings/SettingsLayout";
import { Button as LegacyButton } from "@/src/components/tailwind/Button";
import { toasts } from "@/src/components/toasts/toasts";
import { Button } from "@/src/components/ui/button";
import {
  DealRoleType,
  FirmQuery,
  useDeleteDealRoleMutation,
  useUpdateDealRoleMutation,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { useQueryClient } from "@tanstack/react-query";
import { PlusIcon } from "lucide-react";
import { useState } from "react";
import { CreateDealRoleModal } from "@/src/routes/deal/settings/roles/CreateDealRoleModal";
import {
  EditDealRoleModal,
  EditableDealRole,
} from "@/src/routes/deal/settings/roles/EditDealRoleModal";

type FirmRole = FirmQuery["firm"]["dealRoles"][number];

export function FirmRolesSettings(props: {
  firmId: string;
  roles: FirmRole[];
}) {
  const internalRoles = props.roles.filter(
    (r) => r.type === DealRoleType.Internal,
  );
  const guestRoles = props.roles.filter((r) => r.type === DealRoleType.Guest);

  return (
    <SettingsPage
      title="Roles"
      description="Default roles applied to new deals created by your firm."
    >
      <RoleSection
        title="Admin roles"
        emptyLabel="admin"
        roleType={DealRoleType.Internal}
        roles={internalRoles}
        firmId={props.firmId}
        allowDefault
      />
      <RoleSection
        title="Guest roles"
        emptyLabel="guest"
        roleType={DealRoleType.Guest}
        roles={guestRoles}
        firmId={props.firmId}
      />
    </SettingsPage>
  );
}

function RoleSection(props: {
  title: string;
  emptyLabel: string;
  roleType: DealRoleType;
  roles: FirmRole[];
  firmId: string;
  allowDefault?: boolean;
}) {
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const updateRole = useUpdateDealRoleMutation(client);

  const [editingRole, setEditingRole] = useState<EditableDealRole | null>(
    null,
  );
  const [deletingRole, setDeletingRole] = useState<FirmRole | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  async function makeDefault(role: FirmRole) {
    if (updateRole.isPending || role.isDefault) {
      return;
    }

    try {
      await updateRole.mutateAsync({
        input: {
          id: role.id,
          isDefault: true,
        },
      });
      await queryClient.invalidateQueries({
        queryKey: ["Firm", { id: props.firmId }],
      });
      toasts.success("Default role updated");
    } catch {
      toasts.error("Failed to update default role");
    }
  }

  return (
    <>
      <SettingsCard
        title={props.title}
        description={
          props.emptyLabel === "admin"
            ? "Roles for admin group members"
            : "Roles for guest group members"
        }
        action={
          <Button type="button" size="sm" onClick={() => setShowCreate(true)}>
            <PlusIcon className="mr-1.5 h-3.5 w-3.5" />
            Create
          </Button>
        }
        contentClassName="divide-y divide-border p-0"
      >
        {props.roles.length === 0 ? (
          <SettingsEmpty>
            No {props.emptyLabel} roles configured.
          </SettingsEmpty>
        ) : (
          props.roles.map((role) => (
            <SettingsRow key={role.id}>
              <div className="min-w-0 flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-foreground">
                    {role.name}
                  </p>
                  {props.allowDefault && role.isDefault ? (
                    <DefaultRoleTag />
                  ) : null}
                </div>
                <DealPermissionsSummary
                  permissions={role.permissionLevels}
                  roleType={role.type}
                />
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {props.allowDefault && !role.isDefault ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={updateRole.isPending}
                    onClick={() => makeDefault(role)}
                  >
                    Make default
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingRole(role)}
                >
                  Edit
                </Button>
                {role.isDefault ? null : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setDeletingRole(role)}
                  >
                    Delete
                  </Button>
                )}
              </div>
            </SettingsRow>
          ))
        )}
      </SettingsCard>

      <CreateDealRoleModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        roleType={props.roleType}
        firmId={props.firmId}
      />

      {editingRole ? (
        <EditDealRoleModal
          open
          role={editingRole}
          onClose={() => setEditingRole(null)}
        />
      ) : null}

      {deletingRole ? (
        <DeleteDealRoleModal
          open
          role={deletingRole}
          firmId={props.firmId}
          onClose={() => setDeletingRole(null)}
        />
      ) : null}
    </>
  );
}

function DeleteDealRoleModal(props: {
  open: boolean;
  onClose: () => void;
  role: FirmRole;
  firmId: string;
}) {
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const deleteRole = useDeleteDealRoleMutation(client);

  return (
    <AnimatedModal open={props.open} onClose={props.onClose} size="lg">
      <div className="flex items-center justify-between mb-4">
        <p className="font-semibold text-foreground">Delete role</p>
        <CloseIcon onClose={props.onClose} />
      </div>

      <p className="text-sm text-muted-foreground">
        Are you sure you want to delete{" "}
        <span className="font-semibold text-foreground">{props.role.name}</span>
        ? This removes it from your firm defaults and cannot be undone.
      </p>

      <div className="mt-6 flex justify-end gap-2">
        <LegacyButton text="Cancel" variant="neutral" onClick={props.onClose} />
        <LegacyButton
          text="Delete"
          variant="danger"
          isLoading={deleteRole.isPending}
          onClick={() => {
            if (deleteRole.isPending) {
              return;
            }
            deleteRole.mutate(
              { id: props.role.id },
              {
                onSuccess: () => {
                  toasts.success("Role deleted");
                  queryClient.invalidateQueries({
                    queryKey: ["Firm", { id: props.firmId }],
                  });
                  props.onClose();
                },
                onError: () => {
                  toasts.error("Failed to delete role");
                },
              },
            );
          }}
        />
      </div>
    </AnimatedModal>
  );
}
